// src/pages/FacultyDashboard.jsx
import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useDarkMode, DM } from "../context/DarkModeContext";
import { MonthlyChart, StarPerformerWidget } from "../components/DashboardWidgets";
import { SECTIONS, SECTION_FIELDS } from "../constants/sections";
import { loadAllSections } from "../utils/sectionUtils";
import { INDIAN_STATES_DISTRICTS } from "../constants/indiaStates";
import DashboardHeader from "../components/DashboardHeader";
import { api } from "../utils/api";
import * as XLSX from 'xlsx';

// Compress image to base64 (max 120x120, 50% quality) for Firestore storage
function compressImageToBase64(file) {
  return new Promise((resolve, reject) => {
    const timeout = setTimeout(() => reject(new Error("Image processing timed out. Please try a smaller image.")), 8000);
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        clearTimeout(timeout);
        const maxSize = 120; 
        let w = img.width, h = img.height;
        if (w > h) { if (w > maxSize) { h = Math.round(h * maxSize / w); w = maxSize; } }
        else { if (h > maxSize) { w = Math.round(w * maxSize / h); h = maxSize; } }
        const canvas = document.createElement('canvas');
        canvas.width = w; canvas.height = h;
        const ctx = canvas.getContext('2d');
        if (!ctx) { reject(new Error("Browser does not support canvas scaling")); return; }
        ctx.drawImage(img, 0, 0, w, h);
        try {
          resolve(canvas.toDataURL('image/jpeg', 0.5));
        } catch (err) {
          reject(new Error("Failed to convert image to data URL."));
        }
      };
      img.onerror = () => { clearTimeout(timeout); reject(new Error("Failed to load image file.")); };
      img.src = e.target.result;
    };
    reader.onerror = () => { clearTimeout(timeout); reject(new Error("Failed to read image file.")); };
    reader.readAsDataURL(file);
  });
}


export default function FacultyDashboard() {
  const { currentUser, logout, userRole } = useAuth();
  const { darkMode, toggleDarkMode } = useDarkMode();
  const d = darkMode;
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [sectionData, setSectionData] = useState({}); // Store section data separately
  const [activeSection, setActiveSection] = useState("articlePublications");
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditEntryModal, setShowEditEntryModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState("");
  const [allSections, setAllSections] = useState(SECTIONS);
  const [facultyList, setFacultyList] = useState([]);
  const [activeTab, setActiveTab] = useState("dashboard");
  const [profileFields, setProfileFields] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [colleges, setColleges] = useState(["Adhiparasakthi Dental College and Hospital"]);
  const [designations, setDesignations] = useState([]);
  const [showEditModal, setShowEditModal] = useState(false);
  const [hoverMenu, setHoverMenu] = useState(null);

  const hasRight = (key) => (profile?.rights || []).includes(key);

  async function fetchFacultyList() {
    try {
      const res = await api.get(`/faculty?orderBy=name`);
      const data = Array.isArray(res) ? res : [];
      const defaultArrays = {
        articlePublications: [], textBookPublications: [], patents: [], copyrights: [],
        fellowships: [], phd: [], swayamCourses: [], awards: [], innovativeTeaching: [],
        eContent: [], researchGrants: [], guestLectures: [], conferencePresentation: [], shortResearchProjects: [],
      };
      const list = data
        .map(row => ({ id: row.id, ...defaultArrays, ...(typeof row.data === 'string' ? JSON.parse(row.data) : row.data || row) }))
        .filter(u => u.role === "faculty" || u.role === "admin");
      setFacultyList(list);
      return list;
    } catch (err) { 
      console.error("Failed to fetch faculty list:", err);
      return [];
    }
  }

  useEffect(() => {
    async function init() {
      if (!currentUser) return;
      setLoading(true);
      try {
        // Load basic profile first
        const p = await fetchProfile();
        // Parallel load other required data
        const [fields, sections] = await Promise.all([
          fetchProfileFields(),
          loadAllSections(),
          loadOptions()
        ]);
        
        setAllSections(sections);
        
        // If they have rights to see global stats, fetch everyone
        if (p?.rights?.includes("star_performer") || p?.rights?.includes("charts")) {
          await fetchFacultyList();
        }
      } catch (err) {
        console.error("Initialization error:", err);
      } finally {
        setLoading(false);
      }
    }
    init();
  }, [currentUser]);

  async function fetchProfileFields() {
    try {
      const res = await api.get(`/faculty_profile_fields?orderBy=order`);
      const data = Array.isArray(res) ? res : [];
      setProfileFields(data.map(row => ({ id: row.id, ...(typeof row.data === 'string' ? JSON.parse(row.data) : row.data || row) })));
    } catch (err) { console.error(err); }
    setLoading(false);
  }

  const getNameWithTitle = (p) => {
    if (!p) return "";
    const title = p.title || p.salutation || p.prefix || "";
    if (!title) return p.name || "";
    return `${title} ${p.name}`.trim();
  };
  async function loadOptions() {
    try {
      const dRes = await api.get(`/departments?orderBy=name`);
      const dData = Array.isArray(dRes) ? dRes : [];
      setDepartments(dData.map(d => (typeof d.data === 'string' ? JSON.parse(d.data) : d.data || d).name || d.name || d));
      
      const cRes = await api.get(`/colleges?orderBy=name`);
      const cData = Array.isArray(cRes) ? cRes : [];
      setColleges(["Adhiparasakthi Dental College and Hospital", ...cData.map(c => (typeof c.data === 'string' ? JSON.parse(c.data) : c.data || c).name || c.name || c)]);
      
      const desRes = await api.get(`/designations?orderBy=name`);
      const desData = Array.isArray(desRes) ? desRes : [];
      setDesignations(desData.map(d => (typeof d.data === 'string' ? JSON.parse(d.data) : d.data || d).name || d.name || d));
    } catch {}
  }

  async function fetchProfile() {
    try {
      // Fetch profile info
      const res = await api.get(`/faculty/${currentUser.uid}/profile`);
      if (res) {
        const p = { id: res.id, ...res.data || res };
        setProfile(p);

        // Fetch section data for all sections to ensure accurate counts
        const newSectionData = {};
        const allSectionKeys = SECTIONS.map(s => s.key);
        
        console.log("[FacultyDashboard] Fetching section data for all categories...");
        
        for (const sectionKey of allSectionKeys) {
          try {
            const sectionRes = await api.get(`/faculty/${currentUser.uid}/section/${sectionKey}`);
            const records = Array.isArray(sectionRes) ? sectionRes : [];
            newSectionData[sectionKey] = records.map(row => ({
              id: row.id,
              ...(typeof row.data === 'string' ? JSON.parse(row.data) : row.data || row)
            }));
            console.log(`[FacultyDashboard] ${sectionKey}: ${newSectionData[sectionKey].length} records`);
          } catch (err) {
            console.warn(`[FacultyDashboard] Failed to fetch ${sectionKey}:`, err);
            newSectionData[sectionKey] = [];
          }
        }
        
        setSectionData(newSectionData);
        return p;
      }
      return null;
    } catch (err) {
      console.error("Failed to fetch profile:", err);
      return null;
    }
  }

  function getStat(key) {
    // Use fetched section data for accurate counts
    if (sectionData && sectionData[key] && Array.isArray(sectionData[key])) {
      const count = sectionData[key].length;
      console.log(`[FacultyDashboard] getStat(${key}): ${count}`);
      return count;
    }
    
    // Fallback to profile data if section data not available yet
    if (!profile) return 0;
    const val = profile[key];
    if (Array.isArray(val)) {
      console.log(`[FacultyDashboard] getStat(${key}) from profile: ${val.length}`);
      return val.length;
    }
    console.log(`[FacultyDashboard] getStat(${key}): 0 (no data)`);
    return val || 0;
  }

  function showToast(msg) { setToast(msg); setTimeout(() => setToast(""), 3500); }
  async function saveEditEntry(sectionKey, oldItem, newItem) {
    try {
      // Extract item ID for API call - assuming it has an id field or use index
      const itemId = newItem.id || oldItem.id || Date.now().toString();
      await api.put(`/faculty/${currentUser.uid}/section/${sectionKey}/${itemId}`, { ...newItem, updatedAt: new Date().toISOString() });
      fetchProfile();
      showToast("✅ Entry updated successfully!");
    } catch { showToast("❌ Failed to update"); }
  }

  async function handleLogout() {
    await logout();
    navigate(userRole === "admin" || userRole === "superadmin" ? "/admin" : "/login");
  }

  if (loading) return (
    <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "Georgia, serif", fontSize: 20, color: "#64748b" }}>
      ⏳ Loading your profile...
    </div>
  );

  const activeData = allSections.find(s => s.key === activeSection);

  return (
    <div style={{ minHeight: "100vh", background: DM.pageBg(d), fontFamily: "Georgia, serif", display: "flex", flexDirection: "column", transition: "background 0.3s" }}>

      <DashboardHeader label="Faculty Portal" showNewsTicker={true}>
        {(userRole === "admin" || userRole === "superadmin") && (
          <button onClick={() => navigate("/admin/dashboard")} style={{ background: "#fff", color: "#1d4ed8", border: "none", borderRadius: 10, padding: "7px 14px", fontSize: 15, fontWeight: 800, cursor: "pointer" }}>← Admin Dashboard</button>
        )}
          {/* Reports Dropdown */}
          <div 
            style={{ position: "relative" }} 
            onMouseEnter={() => setHoverMenu("reports")} 
            onMouseLeave={() => setHoverMenu(null)}
          >
            <button style={{ background: "rgba(255,255,255,0.15)", color: "#fff", border: "1px solid rgba(255,255,255,0.3)", borderRadius: 10, padding: "7px 14px", fontSize: 15, fontWeight: 700, cursor: "pointer", display: "flex", gap: 6, alignItems: "center" }}>
              📊 Reports <span style={{ fontSize: 13 }}>▼</span>
            </button>
            {hoverMenu === "reports" && (
              <div style={{ position: "absolute", top: "100%", right: 0, marginTop: 4, background: "#fff", borderRadius: 12, boxShadow: "0 10px 25px rgba(0,0,0,0.15)", overflow: "hidden", minWidth: 220, zIndex: 1000, display: "flex", flexDirection: "column", padding: "8px 0" }}>
                <button onClick={() => navigate("/my-report")} style={{ background: "transparent", border: "none", padding: "12px 20px", textAlign: "left", fontSize: 16, fontWeight: 600, color: "#1e293b", cursor: "pointer" }} onMouseEnter={e => e.currentTarget.style.background = "#f1f5f9"} onMouseLeave={e => e.currentTarget.style.background = "transparent"}>📋 My Report</button>
                <button onClick={() => navigate("/admin/metrics-report")} style={{ background: "transparent", border: "none", padding: "12px 20px", textAlign: "left", fontSize: 16, fontWeight: 600, color: "#1e293b", cursor: "pointer" }} onMouseEnter={e => e.currentTarget.style.background = "#f1f5f9"} onMouseLeave={e => e.currentTarget.style.background = "transparent"}>📈 Metrics Report</button>
                {hasRight("all_faculty_report") && (
                  <button onClick={() => navigate("/admin/report")} style={{ background: "transparent", border: "none", padding: "12px 20px", textAlign: "left", fontSize: 16, fontWeight: 600, color: "#1e293b", cursor: "pointer" }} onMouseEnter={e => e.currentTarget.style.background = "#f1f5f9"} onMouseLeave={e => e.currentTarget.style.background = "transparent"}>📊 All Faculty Reports</button>
                )}
                <div style={{ height: 1, background: "#f1f5f9", margin: "4px 0" }} />
                {(hasRight("staff_details_report") || hasRight("faculty_list") || hasRight("staff_affidavit_dci")) && (
                  <div style={{ padding: "4px 20px 2px", fontSize: 13, fontWeight: 900, color: "#94a3b8", letterSpacing: 1 }}>STAFF REPORTS</div>
                )}
                {hasRight("staff_details_report") && (
                  <>
                    <button onClick={() => navigate("/admin/staff-details?type=dci")} style={{ background: "transparent", border: "none", padding: "10px 20px", textAlign: "left", fontSize: 16, fontWeight: 600, color: "#1e293b", cursor: "pointer", display: "flex", alignItems: "center", gap: 8 }} onMouseEnter={e => e.currentTarget.style.background = "#eaf1ff"} onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                      <span>📋</span> DCI Report
                    </button>
                    <button onClick={() => navigate("/admin/staff-details?type=tnmgr")} style={{ background: "transparent", border: "none", padding: "10px 20px", textAlign: "left", fontSize: 16, fontWeight: 600, color: "#1e293b", cursor: "pointer", display: "flex", alignItems: "center", gap: 8 }} onMouseEnter={e => e.currentTarget.style.background = "#f0fdf4"} onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                      <span>🎓</span> TNMGR University Report
                    </button>
                  </>
                )}
                {hasRight("faculty_list") && (
                  <button onClick={() => navigate("/admin/staff-details?type=faculty_list")} style={{ background: "transparent", border: "none", padding: "10px 20px", textAlign: "left", fontSize: 16, fontWeight: 600, color: "#1e293b", cursor: "pointer", display: "flex", alignItems: "center", gap: 8 }} onMouseEnter={e => e.currentTarget.style.background = "#fff7ed"} onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                    <span>👥</span> Faculty List
                  </button>
                )}
                {hasRight("staff_affidavit_dci") && (
                  <button onClick={() => navigate("/admin/affidavit-dci")} style={{ background: "transparent", border: "none", padding: "10px 20px", textAlign: "left", fontSize: 16, fontWeight: 600, color: "#1e293b", cursor: "pointer", display: "flex", alignItems: "center", gap: 8 }} onMouseEnter={e => e.currentTarget.style.background = "#fffbeb"} onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                    <span>📄</span> Staff Affidavit DCI
                  </button>
                )}
              </div>
            )}
          </div>
          <button onClick={toggleDarkMode} style={{ background: "rgba(255,255,255,0.15)", color: "#fff", border: "1px solid rgba(255,255,255,0.3)", borderRadius: 10, padding: "7px 14px", fontSize: 15, cursor: "pointer" }}>
            {darkMode ? "☀️ Light" : "🌙 Dark"}
          </button>
          <button onClick={handleLogout} style={{ background: "rgba(255,255,255,0.15)", color: "#fff", border: "1px solid rgba(255,255,255,0.3)", borderRadius: 10, padding: "8px 14px", fontWeight: 600, fontSize: 15, cursor: "pointer" }}>🛡️ Logout</button>
      </DashboardHeader>
 
 
      {/* Toast */}
      {toast && (
        <div style={{ position: "fixed", top: 80, right: 24, zIndex: 999, background: toast.startsWith("✅") ? "#f0fdf4" : "#fef2f2", border: `1px solid ${toast.startsWith("✅") ? "#86efac" : "#fecaca"}`, color: toast.startsWith("✅") ? "#16a34a" : "#dc2626", borderRadius: 12, padding: "14px 20px", fontWeight: 700, fontSize: 16, boxShadow: "0 4px 20px rgba(0,0,0,0.1)" }}>
          {toast}
        </div>
      )}

      <div className="dashboard-container" style={{ display: "flex", flex: 1, overflow: "hidden" }}>
        {/* Sidebar */}
        <aside className="dashboard-sidebar" style={{ width: 260, background: DM.sidebarBg(d), boxShadow: DM.shadow(d), display: "flex", flexDirection: "column", transition: "background 0.3s", borderRight: "1px solid #eef2f6", flexShrink: 0 }}>
          

          {/* Profile Curved Box */}
          <div style={{ padding: "0 16px 16px" }}>
            <div style={{ background: "linear-gradient(135deg, #0f2557, #1e40af)", borderRadius: 20, padding: "16px", color: "#fff", boxShadow: "0 10px 20px rgba(15,37,87,0.15)", position: "relative", overflow: "hidden" }}>
              <div style={{ position: "absolute", right: -20, top: -20, width: 80, height: 80, borderRadius: "50%", background: "rgba(255,255,255,0.05)" }} />
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <div style={{ width: 44, height: 44, borderRadius: 14, background: "rgba(255,255,255,0.2)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20, fontWeight: 900, backdropFilter: "blur(4px)", border: "1px solid rgba(255,255,255,0.3)", overflow: "hidden" }}>
                  {profile?.profilePhotoURL
                    ? <img src={profile.profilePhotoURL} alt="Profile" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                    : (profile?.name?.[0]?.toUpperCase() || "F")}
                </div>

                <div style={{ overflow: "hidden" }}>
                  <div style={{ fontWeight: 800, fontSize: 16, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{getNameWithTitle(profile)}</div>
                  <div style={{ fontSize: 13, opacity: 0.7, fontWeight: 600 }}>ID: {profile?.empId}</div>
                </div>
              </div>
            </div>
          </div>

          <div style={{ flex: 1, overflowY: "auto", padding: "0 8px 16px" }}>
            <div style={{ padding: "8px 12px 8px", fontSize: 13, fontWeight: 800, color: DM.textMuted(d), letterSpacing: 1.5 }}>MAIN MENU</div>
            
            {[
              { id: "dashboard", label: "Dashboard", icon: "🏠" },
              { id: "profile", label: "My Profile", icon: "👤" },
              { id: "categories", label: "Categories", icon: "📂" },
            ].map(tab => (
              <div key={tab.id} onClick={() => setActiveTab(tab.id)}
                style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 16px", cursor: "pointer", borderRadius: 12, background: activeTab === tab.id ? "#eff6ff" : "transparent", color: activeTab === tab.id ? "#2563eb" : "#475569", fontWeight: activeTab === tab.id ? 800 : 500, fontSize: 15, marginBottom: 4, transition: "all 0.2s" }}>
                <span style={{ fontSize: 20, filter: activeTab === tab.id ? "grayscale(0)" : "grayscale(1)" }}>{tab.icon}</span>
                <span style={{ flex: 1 }}>{tab.label}</span>
                {activeTab === tab.id && <div style={{ width: 6, height: 6, borderRadius: "50%", background: "#2563eb" }} />}
              </div>
            ))}

            {activeTab === "categories" && (
              <div style={{ marginTop: 16, borderTop: "1px solid #f1f5f9", paddingTop: 16 }}>
                <div style={{ padding: "0 12px 10px", fontSize: 13, fontWeight: 800, color: DM.textMuted(d), letterSpacing: 1.5 }}>SECTIONS</div>
                {allSections.map(sec => (
                  <div key={sec.key} onClick={() => navigate(`/section/${sec.key}`)}
                    style={{ display: "flex", alignItems: "center", gap: 10, padding: "9px 16px", cursor: "pointer", borderRadius: 10, marginLeft: 8, background: activeSection === sec.key ? sec.light : "transparent", color: activeSection === sec.key ? sec.color : "#64748b", fontWeight: activeSection === sec.key ? 700 : 400, fontSize: 14, marginBottom: 2, transition: "all 0.15s" }}>
                    <span style={{ fontSize: 16 }}>{sec.icon}</span>
                    <span style={{ flex: 1 }}>{sec.label}</span>
                    {getStat(sec.key) > 0 && <span style={{ fontSize: 13, opacity: 0.6 }}>({getStat(sec.key)})</span>}
                  </div>
                ))}
              </div>
            )}
          </div>
          
          <div style={{ padding: "16px", borderTop: "1px solid #f1f5f9" }}>
             <button onClick={handleLogout} style={{ width: "100%", background: "#fef2f2", color: "#dc2626", border: "1px solid #fee2e2", borderRadius: 12, padding: "10px", fontWeight: 700, fontSize: 15, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>🚪 Logout</button>
          </div>
        </aside>

        {/* Main */}
        <main className="dashboard-main mobile-padding" style={{ flex: 1, padding: "24px 28px", overflowY: "auto", background: activeTab === "dashboard" ? "#fbfcfe" : DM.pageBg(d) }}>

          {activeTab === "dashboard" && (
            <div>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
                <h1 style={{ fontSize: 24, fontWeight: 800, color: "#0f2557", margin: 0 }}>📊 Dashboard Overview</h1>
                <div style={{ fontSize: 15, color: "#64748b", fontWeight: 600 }}>{new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</div>
              </div>

              {/* Useful Links */}
              <div style={{ background: "#fff", borderRadius: 20, padding: "20px 24px", marginBottom: 24, boxShadow: "0 4px 12px rgba(0,0,0,0.03)", border: "1px solid #f1f5f9" }}>
                <div style={{ fontSize: 13, fontWeight: 800, color: "#94a3b8", letterSpacing: 1.5, marginBottom: 16 }}>🔗 USEFUL QUICK LINKS</div>
                <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                  <a href="https://swayam.gov.in/" target="_blank" rel="noreferrer" style={{ display: "flex", alignItems: "center", gap: 10, background: "#fff7ed", border: "1px solid #ffedd5", borderRadius: 14, padding: "10px 18px", textDecoration: "none", transition: "transform 0.2s" }}>
                    <img src="https://storage.googleapis.com/swayam2_central/assets/img/swayam_logo.png" style={{ height: 30 }} alt="Swayam" />
                    <div><div style={{ fontSize: 15, fontWeight: 800, color: "#ea580c" }}>Swayam</div><div style={{ fontSize: 13, color: "#94a3b8" }}>Online Courses</div></div>
                  </a>
                  <a href="https://scholar.google.com/" target="_blank" rel="noreferrer" style={{ display: "flex", alignItems: "center", gap: 10, background: "#f0fdf4", border: "1px solid #dcfce7", borderRadius: 14, padding: "10px 18px", textDecoration: "none" }}>
                    <img src="https://scholar.google.com/favicon.ico" style={{ height: 26 }} alt="Google Scholar" />
                    <div><div style={{ fontSize: 15, fontWeight: 800, color: "#16a34a" }}>Google Scholar</div><div style={{ fontSize: 13, color: "#94a3b8" }}>Research Articles</div></div>
                  </a>
                  <a href="https://vidwan.inflibnet.ac.in/" target="_blank" rel="noreferrer" style={{ display: "flex", alignItems: "center", gap: 8, background: "#f0f9ff", border: "1.5px solid #7dd3fc", borderRadius: 12, padding: "8px 16px", textDecoration: "none" }}>
                    <img src="https://vidwan.inflibnet.ac.in/images/vidwan-logo.png" onError={e => { e.target.onerror = null; e.target.style.display = 'none'; }} style={{ height: 28 }} alt="Vidwan" />
                    <div>
                      <div style={{ fontSize: 15, fontWeight: 800, color: "#0284c7" }}>Vidwan</div>
                      <div style={{ fontSize: 13, color: "#94a3b8" }}>vidwan.inflibnet.ac.in</div>
                    </div>
                  </a>
                </div>
              </div>

              {/* Widgets Row — Only show if user has the specific rights */}
              {(hasRight("star_performer") || hasRight("charts")) && (
                <div className="mobile-grid-2" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, marginBottom: 24, marginTop: 8 }}>
                  {hasRight("star_performer") && (
                    <StarPerformerWidget facultyList={facultyList} allSections={allSections} darkMode={d} />
                  )}
                  {hasRight("charts") && (
                    <MonthlyChart facultyList={facultyList} darkMode={d} allSections={allSections} />
                  )}
                </div>
              )}

              {/* Stats Summary Grid */}
              <div style={{ fontSize: 13, fontWeight: 800, color: "#94a3b8", letterSpacing: 1.5, marginBottom: 16 }}>📊 PERFORMANCE SUMMARY</div>
              <div className="mobile-grid-4" style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 16 }}>
                {allSections.map(sec => (
                  <div key={sec.key} onClick={() => navigate(`/section/${sec.key}`)}
                    style={{ background: "#fff", borderRadius: 16, padding: "20px", boxShadow: "0 4px 12px rgba(0,0,0,0.03)", border: "1px solid #f1f5f9", cursor: "pointer", transition: "transform 0.2s" }}>
                    <div style={{ fontSize: 26, marginBottom: 8 }}>{sec.icon}</div>
                    <div style={{ fontSize: 26, fontWeight: 900, color: sec.color }}>{getStat(sec.key)}</div>
                    <div style={{ fontSize: 14, color: "#475569", fontWeight: 700, marginTop: 4 }}>{sec.label}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === "profile" && (
            <div>
               <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
                <h1 style={{ fontSize: 24, fontWeight: 800, color: "#0f2557", margin: 0 }}>👤 My Faculty Profile</h1>
                <button onClick={() => setShowEditModal(true)} style={{ background: "#2563eb", color: "#fff", border: "none", borderRadius: 12, padding: "10px 24px", fontWeight: 700, cursor: "pointer", fontSize: 16 }}>✏️ Edit Profile</button>
              </div>

              <div style={{ background: "#fff", borderRadius: 24, boxShadow: "0 10px 40px rgba(0,0,0,0.05)", overflow: "hidden", border: "1px solid #f1f5f9" }}>
                <div style={{ background: "linear-gradient(135deg, #0f2557, #2563eb)", height: 120, position: "relative" }}>
                   <div style={{ position: "absolute", bottom: -50, left: 32, width: 100, height: 100, borderRadius: 30, background: "linear-gradient(135deg, #f7a84f, #f74f6e)", border: "6px solid #fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 42, fontWeight: 900, color: "#fff", boxShadow: "0 10px 20px rgba(0,0,0,0.1)", overflow: "hidden" }}>
                     {profile?.profilePhotoURL
                       ? <img src={profile.profilePhotoURL} alt="Profile" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                       : (profile?.name?.[0]?.toUpperCase() || "F")}
                   </div>

                </div>
                <div style={{ padding: "64px 32px 32px" }}>
                  <div style={{ marginBottom: 32 }}>
                    <h2 style={{ fontSize: 26, fontWeight: 900, color: "#1e293b", margin: 0 }}>{getNameWithTitle(profile)}</h2>
                    <p style={{ fontSize: 16, color: "#64748b", fontWeight: 600, margin: "4px 0 0" }}>{profile?.designation} • {profile?.department}</p>
                  </div>

                  {(() => {
                    const allFields = [
                      { label: "Email Address", val: profile?.email, icon: "✉️", group: "Contact Details" },
                      { label: "Mobile Number", val: profile?.mobile, icon: "📱", group: "Contact Details" },
                      { label: "Department", val: profile?.department, icon: "🏢", group: "Job Details" },
                      { label: "Designation", val: profile?.designation, icon: "🎓", group: "Job Details" },
                      { label: "Institution", val: profile?.institution, icon: "🏛️", group: "Job Details" },
                      ...profileFields.map(f => ({
                        label: f.name,
                        val: profile?.[f.key || f.name.toLowerCase().replace(/[^a-z0-9]/g, "_")],
                        icon: "🔹",
                        group: f.group || "Other Details"
                      }))
                    ];

                    const groups = allFields.reduce((acc, f) => {
                      const g = f.group || "Other Details";
                      if (!acc[g]) acc[g] = [];
                      acc[g].push(f);
                      return acc;
                    }, {});

                    const groupOrder = ["STAFF DETAILS", "PERSONAL DETAILS", "CONTACT DETAILS", "JOB DETAILS"];
                    const sortedGroups = Object.entries(groups).sort(([a], [b]) => {
                      const idxA = groupOrder.indexOf(a.toUpperCase());
                      const idxB = groupOrder.indexOf(b.toUpperCase());
                      if (idxA !== -1 && idxB !== -1) return idxA - idxB;
                      if (idxA !== -1) return -1;
                      if (idxB !== -1) return 1;
                      return a.localeCompare(b);
                    });

                    return (
                      <div className="mobile-grid-3" style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "24px" }}>
                        {sortedGroups.map(([groupName, fields]) => {
                          const filledFields = fields.filter(f => f.val && f.val.toString().trim() !== "");
                          if (filledFields.length === 0) return null;

                          return (
                            <div key={groupName} style={{ background: "#fbfcfe", borderRadius: 20, border: "1.5px solid #eef2f6", overflow: "hidden", display: "flex", flexDirection: "column", boxShadow: "0 4px 12px rgba(0,0,0,0.02)" }}>
                              <div style={{ padding: "14px 20px", background: "#f8fafc", borderBottom: "1px solid #eef2f6", display: "flex", alignItems: "center", gap: 10 }}>
                                 <div style={{ width: 4, height: 18, background: "#2563eb", borderRadius: 2 }}></div>
                                 <span style={{ fontSize: 13, fontWeight: 900, color: "#475569", letterSpacing: 1 }}>{groupName.toUpperCase()}</span>
                              </div>
                              <div style={{ padding: "20px", display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: "16px 24px" }}>
                                {filledFields.map((item, idx) => (
                                  <div key={idx} style={{ minWidth: 0 }}>
                                    <div style={{ fontSize: 13, fontWeight: 800, color: "#94a3b8", letterSpacing: 0.5, marginBottom: 4, display: "flex", alignItems: "center", gap: 6 }}>
                                      <span>{item.icon}</span> {item.label.toUpperCase()}
                                    </div>
                                    <div style={{ fontSize: 17, fontWeight: 700, color: "#1e293b", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }} title={item.val}>
                                      {item.val}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    );
                  })()}

                  {/* Designation History */}
                  {profile?.designationHistory?.length > 0 && (
                    <div style={{ gridColumn: "1 / -1", marginTop: 32 }}>
                      <div style={{ fontSize: 13, fontWeight: 800, color: "#94a3b8", letterSpacing: 1, marginBottom: 12, display: "flex", alignItems: "center", gap: 6 }}>
                        <span>📜</span> INSTITUTIONAL DESIGNATION HISTORY
                      </div>
                      <div style={{ overflowX: "auto", border: "1px solid #f1f5f9", borderRadius: 16 }}>
                        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 15 }}>
                          <thead>
                            <tr style={{ background: "#f8fafc", textAlign: "left" }}>
                              <th style={{ padding: "12px 16px", color: "#64748b" }}>S.No</th>
                              <th style={{ padding: "12px 16px", color: "#64748b" }}>Department</th>
                              <th style={{ padding: "12px 16px", color: "#64748b" }}>Designation</th>
                              <th style={{ padding: "12px 16px", color: "#64748b" }}>Unit</th>
                              <th style={{ padding: "12px 16px", color: "#64748b" }}>Period</th>
                            </tr>
                          </thead>
                          <tbody>
                            {profile.designationHistory.map((des, idx) => (
                              <tr key={idx} style={{ borderTop: "1px solid #f1f5f9", background: des.isMain ? "#f0fdf4" : "transparent" }}>
                                <td style={{ padding: "12px 16px" }}>{idx + 1}</td>
                                <td style={{ padding: "12px 16px" }}>{des.dept}</td>
                                <td style={{ padding: "12px 16px", fontWeight: 700 }}>{des.designation} {des.isMain && <span style={{ fontSize: 13, background: "#16a34a", color: "#fff", borderRadius: 4, padding: "2px 6px", marginLeft: 6 }}>MAIN</span>}</td>
                                <td style={{ padding: "12px 16px" }}>{des.unit}</td>
                                <td style={{ padding: "12px 16px", fontSize: 14 }}>
                                  {(des.fromDate || "").split("-").reverse().join("-") || "—"} to {(des.toDate || "").split("-").reverse().join("-") || "Till Date"}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {activeTab === "categories" && (
            <div>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
                <h1 style={{ fontSize: 24, fontWeight: 800, color: "#0f2557", margin: 0 }}>📂 Resource Categories</h1>
                <button onClick={() => setShowAddModal(true)} style={{ background: activeData?.color, color: "#fff", border: "none", borderRadius: 12, padding: "10px 24px", fontWeight: 700, cursor: "pointer", fontSize: 16 }}>+ Add {activeData?.label}</button>
              </div>

              {/* Profile Banner */}
              <div style={{ background: "linear-gradient(120deg, #0f2557, #1a3a8f, #2563eb)", borderRadius: 20, padding: "24px 32px", color: "#fff", display: "flex", alignItems: "center", gap: 24, marginBottom: 24, boxShadow: "0 8px 32px rgba(37,99,235,0.18)", position: "relative", overflow: "hidden" }}>
                <div style={{ position: "absolute", right: -40, top: -40, width: 200, height: 200, borderRadius: "50%", background: "rgba(255,255,255,0.04)" }} />
                <div style={{ width: 72, height: 72, borderRadius: "50%", background: "linear-gradient(135deg, #f7a84f, #f74f6e)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 30, fontWeight: 900, border: "3px solid rgba(255,255,255,0.35)", flexShrink: 0 }}>
                  {profile?.name?.[0]?.toUpperCase() || "F"}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 24, fontWeight: 900 }}>{getNameWithTitle(profile)}</div>
                  <div style={{ fontSize: 16, opacity: 0.8, fontWeight: 600 }}>Currently Viewing: <strong>{activeData?.label}</strong></div>
                </div>
              </div>

              {/* Stats Grid */}
              <div className="mobile-grid-7" style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 10, marginBottom: 24 }}>
                {allSections.map(sec => (
                  <div key={sec.key} onClick={() => navigate(`/section/${sec.key}`)}
                    style={{ background: activeSection === sec.key ? sec.light : "#fff", borderRadius: 12, padding: "14px 10px", boxShadow: "0 4px 12px rgba(0,0,0,0.03)", borderTop: `3px solid ${sec.color}`, border: activeSection === sec.key ? `2px solid ${sec.color}` : "2px solid #f1f5f9", cursor: "pointer", textAlign: "center", transition: "all 0.18s" }}>
                    <div style={{ fontSize: 20, marginBottom: 4 }}>{sec.icon}</div>
                    <div style={{ fontSize: 24, fontWeight: 900, color: sec.color }}>{getStat(sec.key)}</div>
                    <div style={{ fontSize: 13, color: activeSection === sec.key ? sec.color : "#666", fontWeight: 700, marginTop: 2, lineHeight: 1.2 }}>{sec.label}</div>
                  </div>
                ))}
              </div>

              {/* Section Detail List */}
              <div style={{ background: "#fff", borderRadius: 18, boxShadow: "0 10px 30px rgba(0,0,0,0.04)", overflow: "hidden", border: "1px solid #f1f5f9" }}>
                <div style={{ padding: "18px 24px", borderBottom: "1px solid #f1f5f9", display: "flex", justifyContent: "space-between", alignItems: "center", background: activeData?.light }}>
                  <h2 style={{ fontWeight: 800, fontSize: 18, color: "#0f2557", margin: 0 }}>{activeData?.icon} {activeData?.label} Entries</h2>
                  <button onClick={() => setShowAddModal(true)} style={{ background: activeData?.color, color: "#fff", border: "none", borderRadius: 10, padding: "8px 18px", fontWeight: 700, cursor: "pointer", fontSize: 15 }}>+ Add Entry</button>
                </div>
                <div style={{ padding: "24px" }}>
                  {!sectionData?.[activeSection]?.length ? (
                    <div style={{ textAlign: "center", padding: "40px 0", color: "#94a3b8" }}>
                      <div style={{ fontSize: 42, marginBottom: 12 }}>{activeData?.icon}</div>
                      <p>You haven't added any {activeData?.label} yet.</p>
                      <button onClick={() => setShowAddModal(true)} style={{ marginTop: 12, background: activeData?.light, color: activeData?.color, border: "none", borderRadius: 10, padding: "9px 24px", fontWeight: 700, cursor: "pointer" }}>Add Your First Entry</button>
                    </div>
                  ) : (
                    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                      {sectionData[activeSection].map((item, i) => (
                        <ItemCard key={i} item={item} section={activeSection} sectionData={activeData} index={i + 1} onEdit={(item) => { setEditingItem(item); setShowEditEntryModal(true); }} />
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </main>
      </div>

      {showEditModal && (
        <EditProfileModal
          profile={profile}
          userId={currentUser.uid}
          departments={departments}
          colleges={colleges}
          designations={designations}
          profileFields={profileFields}
          onClose={() => setShowEditModal(false)}
          onSuccess={() => { setShowEditModal(false); fetchProfile(); setToast("✅ Profile updated!"); }}
        />
      )}

      {showAddModal && (
        <AddItemModal
          section={activeSection}
          sectionData={activeData}
          userId={currentUser.uid}
          profile={profile}
          onClose={() => setShowAddModal(false)}
          onSuccess={() => { setShowAddModal(false); fetchProfile(); showToast("✅ Added successfully!"); }}
        />
      )}

      {showEditEntryModal && editingItem && (
        <EditEntryModal
          section={activeSection}
          sectionData={activeData}
          item={editingItem}
          userId={currentUser.uid}
          onClose={() => { setShowEditEntryModal(false); setEditingItem(null); }}
          onSuccess={(oldItem, newItem) => { saveEditEntry(activeSection, oldItem, newItem); setShowEditEntryModal(false); setEditingItem(null); }}
        />
      )}
    </div>
  );
}

function ItemCard({ item, section, sectionData, index, onEdit }) {
  const [fields, setFields] = useState([]);

  useEffect(() => {
    async function loadFields() {
      try {
        const res = await api.get(`/section_fields_${section}?orderBy=order`);
        const data = Array.isArray(res) ? res : [];
        setFields(data.length > 0 ? data.map(row => ({ id: row.id, ...(typeof row.data === 'string' ? JSON.parse(row.data) : row.data || row) })) : (SECTION_FIELDS[section] || []));
      } catch { setFields(SECTION_FIELDS[section] || []); }
    }
    loadFields();
  }, [section]);

  function getKey(f) { return f.key || f.name?.toLowerCase().replace(/[^a-z0-9]/g, "_"); }
  const titleField = fields.find(f => f.required);
  const title = titleField ? item[getKey(titleField)] : Object.values(item).find(v => v && typeof v === "string" && v !== "addedAt" && v !== "updatedAt");

  return (
    <div style={{ background: sectionData?.light, borderRadius: 12, padding: "14px 18px", borderLeft: `4px solid ${sectionData?.color}` }}>
      <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
        <div style={{ width: 26, height: 26, borderRadius: "50%", background: sectionData?.color, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 800, flexShrink: 0 }}>{index}</div>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 700, color: "#1e293b", fontSize: 15, marginBottom: 5 }}>{title || "—"}</div>
          <div className="mobile-grid-3" style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "3px 14px" }}>
            {fields.filter(f => !f.required).map(f => {
              const k = getKey(f);
              const val = item[k];
              const type = (f.type || "").toLowerCase();
              if (!val) return null;
              if ((type === "file" || type === "image") && typeof val === "string" && val.startsWith("http")) {
                const isImage = type === "image" || val.match(/\.(jpeg|jpg|gif|png|webp)/i);
                return (
                  <div key={k} style={{ fontSize: 13, color: "#64748b" }}>
                    {isImage ? (
                      <div style={{ marginTop: 4 }}>
                        <img src={val} alt={f.label || f.name} style={{ width: 80, height: 80, objectFit: "cover", borderRadius: 8, border: "1px solid #e2e8f0" }} />
                        <div style={{ marginTop: 2 }}><a href={val} target="_blank" rel="noreferrer" style={{ color: "#2563eb", fontWeight: 700, textDecoration: "none", fontSize: 12 }}>🔍 Enlarge</a></div>
                      </div>
                    ) : (
                      <a href={val} target="_blank" rel="noreferrer" style={{ color: "#2563eb", fontWeight: 700, textDecoration: "none" }}>📎 View File</a>
                    )}
                  </div>
                );
              }
              return (
                <div key={k} style={{ fontSize: 13, color: "#64748b" }}>
                  <span style={{ fontWeight: 600, color: "#475569" }}>{f.label || f.name}:</span> {val}
                </div>
              );
            })}
          </div>
        </div>
        <div style={{ display: "flex", gap: 6, alignItems: "center", flexShrink: 0 }}>
          {(() => {
            let rawStatus = item.currentStatus || item.current_status;
            if (!rawStatus) {
              const statusKey = Object.keys(item).find(k => k.toLowerCase().includes("status"));
              if (statusKey) rawStatus = item[statusKey];
            }
            if (!rawStatus) return null;
            
            const isSuccess = ["Published", "Completed", "Received"].includes(rawStatus);
            const isWarning = ["In Progress", "Accepted", "Nominated", "Initiated"].includes(rawStatus);
            
            return (
              <span style={{ 
                background: isSuccess ? "#dcfce7" : isWarning ? "#fef3c7" : "#f1f5f9", 
                color: isSuccess ? "#16a34a" : isWarning ? "#d97706" : "#64748b", 
                borderRadius: 20, 
                padding: "3px 10px", 
                fontSize: 13, 
                fontWeight: 700 
              }}>
                {rawStatus}
              </span>
            );
          })()}
          {item.affidavit && (
            <span style={{ 
              background: "#fef2f2", 
              color: "#dc2626", 
              borderRadius: 8, 
              padding: "3px 10px", 
              fontSize: 13, 
              fontWeight: 800, 
              display: "flex", 
              alignItems: "center", 
              gap: 4,
              border: "1px solid #fecaca" 
            }}>
              📄 Affidavit Selected
            </span>
          )}
          <button onClick={() => onEdit(item)}
            style={{ background: "#eaf1ff", color: "#2563eb", border: "none", borderRadius: 8, padding: "5px 10px", fontWeight: 700, cursor: "pointer", fontSize: 14 }}>
            ✏️ Edit
          </button>
        </div>
      </div>
    </div>
  );
}

export function AddItemModal({ section, sectionData, userId, profile, onClose, onSuccess }) {
  const [form, setForm] = useState({});
  const formDataRef = useRef({}); // Keep track of current form data to avoid stale closures
  const uncontrolledRef = useRef({}); // Nuclear option: uncontrolled inputs store values directly in ref
  const [pendingFiles, setPendingFiles] = useState({});
  const [loading, setLoading] = useState(false);
  const [fieldsLoading, setFieldsLoading] = useState(true);
  const [error, setError] = useState("");
  const [fields, setFields] = useState([]);
  const [doiStatus, setDoiStatus] = useState(null); // null | "validating" | "valid" | "invalid"
  const [doiData, setDoiData] = useState(null);
  
  // Update both state (for rendering) and ref (for always-current access)
  // IMPORTANT: Use BOTH ref + functional setState to guarantee data persists during rapid input
  const set = (k, v) => {
    formDataRef.current = { ...formDataRef.current, [k]: v };  // Update ref synchronously (always current)
    uncontrolledRef.current[k] = v;  // Also update uncontrolled ref as backup
    setForm(prev => ({ ...prev, [k]: v }));  // Use functional setState for React guarantee
  };

  // Helper to find the actual dynamic key for a field by matching name keywords
  function findFieldKey(nameKeywords) {
    for (const keyword of nameKeywords) {
      const found = fields.find(f => (f.name || "").toLowerCase().includes(keyword) || (f.label || "").toLowerCase().includes(keyword));
      if (found) return found.key || getKey(found.name);
    }
    return null;
  }

  async function validateDOI(doi) {
    if (!doi.trim()) { setDoiStatus(null); setDoiData(null); return; }
    setDoiStatus("validating");
    try {
      const cleanDoi = doi.replace(/^https?:\/\/doi\.org\//i, "");
      const res = await fetch(`https://api.crossref.org/works/${encodeURIComponent(cleanDoi)}`);
      if (res.ok) {
        const data = await res.json();
        const work = data.message;
        setDoiData({
          title: work.title?.[0] || "",
          journal: work["container-title"]?.[0] || "",
          year: work.published?.["date-parts"]?.[0]?.[0]?.toString() || "",
          authors: work.author?.map(a => `${a.given || ""} ${a.family || ""}`.trim()).join(", ") || "",
        });
        setDoiStatus("valid");

        // Auto-fill fields using actual dynamic keys from admin config
        const titleKey = findFieldKey(["title of article", "title"]) || "title_of_article";
        const journalKey = findFieldKey(["journal details", "journal", "name of publication"]) || "journal_details";
        const authorKey = findFieldKey(["author name", "author", "authors"]) || "author_name_and_position";

        formDataRef.current = { ...formDataRef.current, doi_validated: "true" };
        if (!formDataRef.current[titleKey]) formDataRef.current[titleKey] = work.title?.[0] || "";
        if (!formDataRef.current[journalKey]) formDataRef.current[journalKey] = work["container-title"]?.[0] || "";
        if (!formDataRef.current[authorKey]) formDataRef.current[authorKey] = work.author?.map(a => `${a.given || ""} ${a.family || ""}`.trim()).join(", ") || "";
        setForm({ ...formDataRef.current });
      } else {
        setDoiStatus("invalid");
        setDoiData(null);
        formDataRef.current = { ...formDataRef.current, doi_validated: "false" };
        setForm({ ...formDataRef.current });
      }
    } catch {
      setDoiStatus("invalid");
      setDoiData(null);
    }
  }

  useEffect(() => {
    async function loadFields() {
      try {
        const res = await api.get(`/section_fields_${section}?orderBy=order`);
        const data = Array.isArray(res) ? res : [];
        if (data.length > 0) {
          setFields(data.map(row => ({ id: row.id, ...(typeof row.data === 'string' ? JSON.parse(row.data) : row.data || row) })));
        } else {
          setFields(SECTION_FIELDS[section] || []);
        }
      } catch {
        setFields(SECTION_FIELDS[section] || []);
      }
      setFieldsLoading(false);
    }
    loadFields();
  }, [section]);

  function getKey(name) {
    return (name || "").toLowerCase().replace(/[^a-z0-9]/g, "_");
  }

  // Helper to resolve key from a field object
  function getFieldKey(f) {
    return f.key || getKey(f.name);
  }

  async function handleAdd() {
    setError(""); // Clear previous errors
    
    // Try formDataRef first, fallback to uncontrolledRef
    let currentFormData = { ...formDataRef.current };
    
    // Fallback: if formDataRef is empty but uncontrolledRef has data, use that
    if (Object.keys(currentFormData).length === 0 && Object.keys(uncontrolledRef.current).length > 0) {
      console.log("[AddItemModal] formDataRef was empty, using uncontrolledRef fallback");
      currentFormData = { ...uncontrolledRef.current };
    }
    
    console.log("[AddItemModal] handleAdd() called");
    console.log("[AddItemModal] formDataRef.current:", formDataRef.current);
    console.log("[AddItemModal] uncontrolledRef.current:", uncontrolledRef.current);
    console.log("[AddItemModal] Final data being validated:", currentFormData);

    // Helper to check if a value is empty
    function isValueEmpty(val) {
      if (val === undefined || val === null || val === "") return true;
      return String(val).trim() === "";
    }

    const requiredField = fields.find(f => {
      const k = getFieldKey(f);
      const fType = (f.type || "").toLowerCase();
      if (!f.required || fType === "file" || fType === "image") return false;
      const val = currentFormData[k];
      const empty = isValueEmpty(val);
      if (empty) {
        console.log("[Validation Debug] Failed field:", f.name || f.label, "| key:", k, "| value type:", typeof val, "| raw value:", val);
        setError(`${f.label || f.name} is required (Debug check value: '${val === undefined ? "undefined" : val}', key: '${k}')`);
      }
      return empty;
    });
    if (requiredField) return; // Error already set above

    const requiredFileField = fields.find(f => {
      const k = getFieldKey(f);
      const fType = (f.type || "").toLowerCase();
      return (f.required && (fType === "file" || fType === "image")) && !pendingFiles[k];
    });
    if (requiredFileField) return setError(`${requiredFileField.label || requiredFileField.name} is required`);

    // DOI mandatory when status is Published (for articlePublications)
    const statusKey = Object.keys(currentFormData).find(k => k.includes("status") || k.includes("current"));
    const isPublished = statusKey && currentFormData[statusKey] === "Published";
    const doiField = fields.find(f => (f.name || "").toLowerCase().includes("doi") || (f.key || "").toLowerCase().includes("doi"));
    const doiKey = doiField ? getFieldKey(doiField) : null;

    if (isPublished && doiKey && !currentFormData[doiKey]?.trim()) {
      return setError("DOI is mandatory when status is Published.");
    }
    if (isPublished && doiKey && currentFormData[doiKey] && doiStatus !== "valid") {
      return setError("Please verify the DOI before saving a Published article.");
    }

    try {
      setLoading(true);
      
      // Prepare data for submission - SEND JSON, NOT FormData
      const dataToSubmit = {
        ...currentFormData,
        addedAt: new Date().toISOString()
      };
      
      // Log what we're sending
      console.log(`[AddItemModal] Submitting JSON for section: ${section}`);
      console.log(`[AddItemModal] Data being sent:`, dataToSubmit);
      console.log(`[AddItemModal] Data keys:`, Object.keys(dataToSubmit));
      console.log(`[AddItemModal] Data entry count:`, Object.keys(dataToSubmit).length);
      
      // Send as JSON
      const response = await api.post(`/faculty/${userId}/section/${section}`, dataToSubmit);
      console.log(`[AddItemModal] API response:`, response);
      onSuccess();
    } catch (err) { 
      console.error("[AddItemModal] Error:", err); 
      setError("Failed to add. Try again."); 
    }
    finally { setLoading(false); }
  }

  const inputStyle = { width: "100%", background: "#f8fafc", border: "1.5px solid #e2e8f0", borderRadius: 10, padding: "9px 12px", fontSize: 15, color: "#1e293b", outline: "none", boxSizing: "border-box", fontFamily: "Georgia, serif" };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 999, padding: 20 }}>
      <div className="mobile-modal" style={{ background: "#fff", borderRadius: 24, padding: "32px", width: "100%", maxWidth: 620, boxShadow: "0 32px 64px rgba(0,0,0,0.2)", maxHeight: "90vh", overflowY: "auto" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
          <h2 style={{ fontWeight: 800, color: "#0f2557", fontSize: 22 }}>{sectionData?.icon} Add {sectionData?.label}</h2>
          <span onClick={onClose} style={{ fontSize: 24, cursor: "pointer", color: "#94a3b8" }}>✕</span>
        </div>

        {error && <div style={{ background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 10, padding: "10px 14px", color: "#dc2626", fontSize: 15, marginBottom: 16 }}>{error}</div>}

        {fieldsLoading ? (
          <div style={{ textAlign: "center", padding: "40px 0", color: "#94a3b8" }}>⏳ Loading fields...</div>
        ) : fields.length === 0 ? (
          <div style={{ textAlign: "center", padding: "40px 0", color: "#94a3b8" }}>
            <p>No fields configured by admin yet.</p>
            <p style={{ fontSize: 14, marginTop: 8 }}>Admin can add fields in Settings → Section Fields</p>
          </div>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
            {fields.map((f, i) => {
              const k = f.key || getKey(f.name);
              const fieldType = (f.type || "text").toLowerCase();
              const label = f.label || f.name;
              return (
                <div key={i} style={{ gridColumn: fieldType === "textarea" ? "1 / -1" : "auto" }}>
                  <label style={{ fontSize: 14, fontWeight: 700, color: "#374151", display: "block", marginBottom: 5 }}>
                    {label} {f.required && <span style={{ color: "#dc2626" }}>*</span>}
                  </label>
                  {fieldType === "dropdown" ? (
                    <select value={form[k] || ""} onChange={e => set(k, e.target.value)} style={{ ...inputStyle, cursor: "pointer" }}>
                      <option value="">-- Select --</option>
                      {(f.options || []).map(o => <option key={o} value={o}>{o}</option>)}
                    </select>
                  ) : fieldType === "textarea" ? (
                    <textarea value={form[k] || ""} onChange={e => set(k, e.target.value)} rows={3} style={{ ...inputStyle, resize: "vertical" }} />
                    ) : fieldType === "image" ? (
                    <div>
                      <input type="file" accept="image/*" onChange={e => {
                        const file = e.target.files[0];
                        if (file) {
                          if (file.size > 200 * 1024) {
                            alert("❌ Image size must be below 200KB");
                            e.target.value = "";
                            return;
                          }
                          if (!file.type.startsWith("image/")) {
                            alert("❌ Please select a valid image file");
                            e.target.value = "";
                            return;
                          }
                          setPendingFiles(prev => ({ ...prev, [k]: file }));
                        }
                      }} style={{ ...inputStyle, padding: "6px 12px" }} />
                      {pendingFiles[k] && (
                        <div style={{ marginTop: 8 }}>
                          <img src={URL.createObjectURL(pendingFiles[k])} alt="Preview" style={{ width: 100, height: 100, objectFit: "cover", borderRadius: 10, border: "1px solid #e2e8f0" }} />
                          <div style={{ fontSize: 13, color: "#16a34a", marginTop: 4 }}>✅ size: {(pendingFiles[k].size / 1024).toFixed(1)}KB</div>
                        </div>
                      )}
                    </div>
                  ) : fieldType === "file" ? (
                    <div>
                      <input type="file" onChange={e => setPendingFiles(prev => ({ ...prev, [k]: e.target.files[0] }))} style={{ ...inputStyle, padding: "6px 12px" }} />
                      {pendingFiles[k] && <div style={{ fontSize: 13, color: "#16a34a", marginTop: 4 }}>📎 Selected: {pendingFiles[k].name}</div>}
                    </div>
                  ) : fieldType === "number" ? (
                    <input type="number" value={form[k] || ""} onChange={e => set(k, e.target.value)} style={inputStyle} />
                  ) : fieldType === "date" ? (
                    <input type="date" value={form[k] || ""} onChange={e => set(k, e.target.value)} style={inputStyle} />
                  ) : fieldType === "month-year" ? (
                    <input type="month" value={form[k] || ""} onChange={e => set(k, e.target.value)} style={{ ...inputStyle, colorScheme: "light", color: "#1e293b" }} />
                  ) : (fieldType === "text" || fieldType === "") && ((f.name || "").toLowerCase().includes("doi") || k.toLowerCase().includes("doi")) ? (
                    <div>
                      {(() => {
                        const statusKey = Object.keys(form).find(k2 => k2.includes("status") || k2.includes("current"));
                        const isPublished = statusKey && form[statusKey] === "Published";
                        return isPublished && (
                          <div style={{ background: "#fef3c7", border: "1px solid #fde68a", borderRadius: 6, padding: "4px 10px", marginBottom: 6, fontSize: 13, color: "#d97706", fontWeight: 600 }}>
                            ⚠️ DOI is required for Published articles
                          </div>
                        );
                      })()}
                      <div style={{ display: "flex", gap: 8 }}>
                        <input type="text" value={form[k] || ""}
                          onChange={e => { set(k, e.target.value); setDoiStatus(null); setDoiData(null); }}
                          onFocus={() => { if (doiStatus) { setDoiStatus(null); setDoiData(null); } }}
                          placeholder="e.g. 10.1038/nature12373"
                          style={{ ...inputStyle, flex: 1 }} />
                        <button type="button"
                          onClick={() => validateDOI(form[k] || "")}
                          disabled={doiStatus === "validating" || !form[k]}
                          style={{ background: doiStatus === "valid" ? "#16a34a" : doiStatus === "invalid" ? "#dc2626" : "linear-gradient(135deg,#1d4ed8,#2563eb)", color: "#fff", border: "none", borderRadius: 10, padding: "8px 14px", fontWeight: 700, cursor: doiStatus === "validating" || !form[k] ? "not-allowed" : "pointer", fontSize: 14, whiteSpace: "nowrap", flexShrink: 0 }}>
                          {doiStatus === "validating" ? "⏳ Checking..." : doiStatus === "valid" ? "✅ AI Verified" : doiStatus === "invalid" ? "❌ Invalid" : "🤖 AI Verify"}
                        </button>
                      </div>
                      {doiStatus === "valid" && doiData && (
                        <div style={{ background: "#f0fdf4", border: "1px solid #86efac", borderRadius: 8, padding: "10px 12px", marginTop: 8 }}>
                          <div style={{ fontSize: 13, fontWeight: 700, color: "#16a34a", marginBottom: 4 }}>🤖 AI Verified — Details Auto-filled</div>
                          {doiData.title && <div style={{ fontSize: 14, color: "#1e293b", fontWeight: 600 }}>📄 {doiData.title}</div>}
                          {doiData.journal && <div style={{ fontSize: 13, color: "#64748b", marginTop: 2 }}>📰 {doiData.journal}</div>}
                          {doiData.authors && <div style={{ fontSize: 13, color: "#64748b", marginTop: 2 }}>👤 {doiData.authors}</div>}
                          {doiData.year && <div style={{ fontSize: 13, color: "#94a3b8", marginTop: 2 }}>📅 {doiData.year}</div>}
                        </div>
                      )}
                      {doiStatus === "invalid" && (
                        <div style={{ background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 8, padding: "8px 12px", marginTop: 8, fontSize: 14, color: "#dc2626" }}>
                          ❌ DOI not found. Please check and try again.
                        </div>
                      )}
                    </div>
                  ) : (
                    <input type="text" value={form[k] || ""} onChange={e => set(k, e.target.value)} style={inputStyle} />
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Affidavit Checkbox */}
        <div style={{ 
          marginTop: 20, 
          padding: "16px", 
          background: "#fff5f5", 
          borderRadius: 16, 
          border: "1.5px dashed #feb2b2",
          display: "flex",
          alignItems: "center",
          gap: 12,
          cursor: "pointer"
        }} onClick={() => set("affidavit", !form.affidavit)}>
          <input 
            type="checkbox" 
            checked={!!form.affidavit} 
            onChange={(e) => set("affidavit", e.target.checked)}
            style={{ width: 20, height: 20, cursor: "pointer" }}
          />
          <div>
            <div style={{ fontWeight: 800, color: "#c53030", fontSize: 16 }}>Affidavit</div>
            <div style={{ fontSize: 13, color: "#e53e3e", fontWeight: 600 }}>Note: Click the checkbox only for Last three years publications</div>
          </div>
        </div>

        <div style={{ display: "flex", gap: 12, marginTop: 24 }}>
          <button onClick={onClose} style={{ flex: 1, background: "#f1f5f9", color: "#475569", border: "none", borderRadius: 12, padding: "12px", fontWeight: 700, cursor: "pointer" }}>Cancel</button>
          <button onClick={handleAdd} disabled={loading || fieldsLoading} style={{ flex: 2, background: loading ? "#93c5fd" : `linear-gradient(135deg, ${sectionData?.color}, ${sectionData?.color}cc)`, color: "#fff", border: "none", borderRadius: 12, padding: "12px", fontWeight: 800, cursor: loading ? "not-allowed" : "pointer", fontFamily: "Georgia, serif" }}>
            {loading ? "⏳ Saving..." : "✅ Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

export function EditEntryModal({ section, sectionData, item, userId, onClose, onSuccess }) {
  const [form, setForm] = useState({ ...item });
  const formDataRef = useRef({ ...item }); // Keep track of current form data to avoid stale closures
  const [pendingFiles, setPendingFiles] = useState({});
  const [loading, setLoading] = useState(false);
  const [fieldsLoading, setFieldsLoading] = useState(true);
  const [error, setError] = useState("");
  const [fields, setFields] = useState([]);
  
  // Update both state (for rendering) and ref (for always-current access)
  // IMPORTANT: Use BOTH ref + functional setState to guarantee data persists during rapid input
  const set = (k, v) => {
    formDataRef.current = { ...formDataRef.current, [k]: v };  // Update ref synchronously (always current)
    setForm(prev => ({ ...prev, [k]: v }));  // Use functional setState for React guarantee
  };

  useEffect(() => {
    async function loadFields() {
      try {
        const res = await api.get(`/section_fields_${section}?orderBy=order`);
        const data = Array.isArray(res) ? res : [];
        setFields(data.length > 0 ? data.map(row => ({ id: row.id, ...(typeof row.data === 'string' ? JSON.parse(row.data) : row.data || row) })) : (SECTION_FIELDS[section] || []));
      } catch { setFields(SECTION_FIELDS[section] || []); }
      setFieldsLoading(false);
    }
    loadFields();
  }, [section]);

  function getKey(f) { return f.key || f.name?.toLowerCase().replace(/[^a-z0-9]/g, "_"); }

  async function handleSave() {
    setError(""); // Clear previous errors
    
    // Always use ref for current form data to avoid stale state closures
    const currentFormData = formDataRef.current;
    console.log("[EditEntryModal] handleSave() called with form data from ref:", currentFormData);
    
    const req = fields.find(f => {
      const fType = (f.type || "").toLowerCase();
      if (!f.required || fType === "file" || fType === "image") return false;
      const val = currentFormData[getKey(f)];
      const empty = (val === undefined || val === null || String(val).trim() === "");
      if (empty) {
        setError(`${f.label || f.name} is required (Debug check value: '${val === undefined ? "undefined" : val}', key: '${getKey(f)}')`);
      }
      return empty;
    });
    if (req) return; // Error already set above
    try {
      setLoading(true);
      
      // Prepare data for submission - SEND JSON, NOT FormData
      const dataToSubmit = {
        ...currentFormData,
        updatedAt: new Date().toISOString()
      };
      
      // Log what we're sending
      console.log(`[EditEntryModal] Updating section: ${section}`);
      console.log(`[EditEntryModal] Data being sent:`, dataToSubmit);
      
      const itemId = item.id || Date.now().toString();
      const response = await api.put(`/faculty/${userId}/section/${section}/${itemId}`, dataToSubmit);
      console.log(`[EditEntryModal] API response:`, response);
      onSuccess(item, currentFormData);
    } catch (err) { 
      console.error("[EditEntryModal] Error:", err); 
      setError("Failed to update."); 
    }
    finally { setLoading(false); }
  }

  const inputStyle = { width: "100%", background: "#f8fafc", border: "1.5px solid #e2e8f0", borderRadius: 10, padding: "9px 12px", fontSize: 15, color: "#1e293b", outline: "none", boxSizing: "border-box", fontFamily: "Georgia, serif" };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 999, padding: 20 }}>
      <div style={{ background: "#fff", borderRadius: 24, padding: "32px", width: "100%", maxWidth: 620, maxHeight: "90vh", overflowY: "auto", boxShadow: "0 32px 64px rgba(0,0,0,0.2)" }}>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 22 }}>
          <h2 style={{ fontWeight: 800, color: "#0f2557", fontSize: 22 }}>{sectionData?.icon} Edit {sectionData?.label}</h2>
          <span onClick={onClose} style={{ fontSize: 24, cursor: "pointer", color: "#94a3b8" }}>✕</span>
        </div>
        {error && <div style={{ background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 10, padding: "10px", color: "#dc2626", fontSize: 15, marginBottom: 14 }}>{error}</div>}
        {fieldsLoading ? <div style={{ textAlign: "center", padding: "30px 0", color: "#94a3b8" }}>⏳ Loading...</div> : (
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            {fields.map((f, i) => {
              const k = getKey(f);
              const type = (f.type || "text").toLowerCase();
              return (
                <div key={i} style={{ gridColumn: type === "textarea" ? "1/-1" : "auto" }}>
                  <label style={{ fontSize: 14, fontWeight: 700, color: "#374151", display: "block", marginBottom: 5 }}>
                    {f.label || f.name} {f.required && <span style={{ color: "#dc2626" }}>*</span>}
                  </label>
                  {type === "dropdown" ? (
                    <select value={form[k] || ""} onChange={e => set(k, e.target.value)} style={{ ...inputStyle, cursor: "pointer" }}>
                      <option value="">-- Select --</option>
                      {(f.options || []).map(o => <option key={o} value={o}>{o}</option>)}
                    </select>
                  ) : type === "textarea" ? (
                    <textarea value={form[k] || ""} onChange={e => set(k, e.target.value)} rows={3} style={{ ...inputStyle, resize: "vertical" }} />
                    ) : type === "image" ? (
                      <div>
                        {form[k] && typeof form[k] === "string" && form[k].startsWith("http") && !pendingFiles[k] && (
                          <div style={{ marginBottom: 10 }}>
                            <img src={form[k]} alt="Current" style={{ width: 100, height: 100, objectFit: "cover", borderRadius: 10, border: "1px solid #e2e8f0" }} />
                          </div>
                        )}
                        <input type="file" accept="image/*" onChange={e => {
                          const file = e.target.files[0];
                          if (file) {
                            if (file.size > 200 * 1024) {
                              alert("❌ Image size must be below 200KB");
                              e.target.value = "";
                              return;
                            }
                            if (!file.type.startsWith("image/")) {
                              alert("❌ Please select a valid image file");
                              e.target.value = "";
                              return;
                            }
                            setPendingFiles(prev => ({ ...prev, [k]: file }));
                          }
                        }} style={{ ...inputStyle, padding: "6px 12px" }} />
                        {pendingFiles[k] && (
                          <div style={{ marginTop: 8 }}>
                            <img src={URL.createObjectURL(pendingFiles[k])} alt="Preview" style={{ width: 100, height: 100, objectFit: "cover", borderRadius: 10, border: "1px solid #e2e8f0" }} />
                            <div style={{ fontSize: 13, color: "#16a34a", marginTop: 4 }}>✅ size: {(pendingFiles[k].size / 1024).toFixed(1)}KB</div>
                          </div>
                        )}
                      </div>
                    ) : type === "file" ? (
                      <div>
                        {form[k] && typeof form[k] === "string" && form[k].startsWith("http") && (
                          <div style={{ fontSize: 13, marginBottom: 8, display: "flex", alignItems: "center", gap: 6 }}>
                            <span style={{ color: "#16a34a", fontWeight: 700 }}>✅ Current file: </span>
                            <a href={form[k]} target="_blank" rel="noreferrer" style={{ color: "#2563eb", textDecoration: "none", fontWeight: 600 }}>View</a>
                            <span style={{ color: "#94a3b8" }}>(Select new to replace)</span>
                          </div>
                        )}
                        <input type="file" onChange={e => setPendingFiles(prev => ({ ...prev, [k]: e.target.files[0] }))} style={{ ...inputStyle, padding: "6px 12px" }} />
                        {pendingFiles[k] && <div style={{ fontSize: 13, color: "#16a34a", marginTop: 4 }}>📎 New file selected: {pendingFiles[k].name}</div>}
                      </div>
                  ) : type === "number" ? (
                    <input type="number" value={form[k] || ""} onChange={e => set(k, e.target.value)} style={inputStyle} />
                  ) : type === "date" ? (
                    <input type="date" value={form[k] || ""} onChange={e => set(k, e.target.value)} style={inputStyle} />
                  ) : (type === "month-year" || type === "Month-Year") ? (
                    <input type="month" value={form[k] || ""} onChange={e => set(k, e.target.value)} style={{ ...inputStyle, colorScheme: "light", color: "#1e293b" }} />
                  ) : (
                    <input type="text" value={form[k] || ""} onChange={e => set(k, e.target.value)} style={inputStyle} />
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Affidavit Checkbox */}
        <div style={{ 
          marginTop: 20, 
          padding: "16px", 
          background: "#fff5f5", 
          borderRadius: 16, 
          border: "1.5px dashed #feb2b2",
          display: "flex",
          alignItems: "center",
          gap: 12,
          cursor: "pointer"
        }} onClick={() => set("affidavit", !form.affidavit)}>
          <input 
            type="checkbox" 
            checked={!!form.affidavit} 
            onChange={(e) => set("affidavit", e.target.checked)}
            style={{ width: 20, height: 20, cursor: "pointer" }}
          />
          <div>
            <div style={{ fontWeight: 800, color: "#c53030", fontSize: 16 }}>Affidavit</div>
            <div style={{ fontSize: 13, color: "#e53e3e", fontWeight: 600 }}>Note: Click the checkbox only for Last three years publications</div>
          </div>
        </div>

        <div style={{ display: "flex", gap: 12, marginTop: 22 }}>
          <button onClick={onClose} style={{ flex: 1, background: "#f1f5f9", color: "#475569", border: "none", borderRadius: 12, padding: "12px", fontWeight: 700, cursor: "pointer" }}>Cancel</button>
          <button onClick={handleSave} disabled={loading} style={{ flex: 2, background: `linear-gradient(135deg,${sectionData?.color},${sectionData?.color}cc)`, color: "#fff", border: "none", borderRadius: 12, padding: "12px", fontWeight: 800, cursor: "pointer", fontFamily: "Georgia, serif" }}>
            ✅ Save Changes
          </button>
        </div>
      </div>
    </div>
  );
}

export function ImportModal({ section, sectionData, userId, onClose, onSuccess }) {
  const [loading, setLoading] = useState(false);
  const [previewData, setPreviewData] = useState([]);
  const [importProgress, setImportProgress] = useState(0);
  const [totalToImport, setTotalToImport] = useState(0);
  const [importStatus, setImportStatus] = useState(null); // null | "preview" | "importing" | "complete"
  const [successCount, setSuccessCount] = useState(0);
  const [failureCount, setFailureCount] = useState(0);
  const [errors, setErrors] = useState([]);

  // Article Publications field mapping
  const ARTICLE_FIELDS = [
    "title_of_article",
    "author_names",
    "author_position",
    "journal_details",
    "name_of_publication",
    "volume",
    "page_no",
    "type_of_article",
    "month_and_year_of_publication",
    "digital_object_identifier",
    "doi",
    "citations",
    "points",
    "current_status",
    "indexing",
    "impact_factor",
    "dci_category_and_impact_factor",
    "link_for_article"
  ];

  function downloadTemplate() {
    const headers = [
      "Title of Article",
      "Author Names",
      "Author Position",
      "Journal Details",
      "Name of Publication",
      "Volume",
      "Page No",
      "Type of Article",
      "Month and Year of Publication",
      "Digital Object Identifier (DOI)",
      "Citations",
      "Points",
      "Current Status",
      "Indexing",
      "Impact Factor",
      "Link for Article"
    ];

    const ws = XLSX.utils.aoa_to_sheet([headers]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Articles");
    XLSX.writeFile(wb, "article-import-template.xlsx");
  }

  async function handleFileSelect(event) {
    const file = event.target.files[0];
    if (!file) return;

    try {
      setLoading(true);
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data, { type: "array" });
      const worksheet = workbook.Sheets[workbook.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(worksheet, { defval: "" });

      if (rows.length === 0) {
        alert("❌ No data found in the file");
        setLoading(false);
        return;
      }

      // Normalize headers to lowercase and map to field names
      const normalizedRows = rows.map(row => {
        const normalized = {};
        for (const [key, value] of Object.entries(row)) {
          const lowerKey = key.toLowerCase().replace(/[^a-z0-9]/g, "_");
          normalized[lowerKey] = value;
        }
        return normalized;
      });

      setPreviewData(normalizedRows);
      setTotalToImport(normalizedRows.length);
      setImportStatus("preview");
    } catch (err) {
      console.error("File read error:", err);
      alert("❌ Failed to read file: " + err.message);
    } finally {
      setLoading(false);
    }
  }

  async function handleImport() {
    setImportStatus("importing");
    setSuccessCount(0);
    setFailureCount(0);
    setErrors([]);

    let success = 0;
    let failures = 0;
    const errorList = [];

    for (let i = 0; i < previewData.length; i++) {
      const row = previewData[i];
      setImportProgress(i + 1);

      // Check required field
      const titleField = row.title_of_article || row.title || "";
      if (!titleField.trim()) {
        failures++;
        errorList.push(`Row ${i + 1}: Missing "Title of Article"`);
        continue;
      }

      try {
        // Prepare data for API
        const dataToSubmit = {
          title_of_article: row.title_of_article || row.title || "",
          author_names: row.author_names || "",
          author_position: row.author_position || "",
          journal_details: row.journal_details || "",
          name_of_publication: row.name_of_publication || "",
          volume: row.volume || "",
          page_no: row.page_no || "",
          type_of_article: row.type_of_article || "",
          month_and_year_of_publication: row.month_and_year_of_publication || "",
          digital_object_identifier: row.digital_object_identifier || row.doi || "",
          citations: row.citations || "",
          points: row.points || "",
          current_status: row.current_status || "Draft",
          indexing: row.indexing || "",
          impact_factor: row.impact_factor || row.dci_category_and_impact_factor || "",
          link_for_article: row.link_for_article || ""
        };

        const response = await api.post(`/faculty/${userId}/section/${section}`, dataToSubmit);
        if (response.success || response.id) {
          success++;
        } else {
          failures++;
          errorList.push(`Row ${i + 1}: API returned no ID`);
        }
      } catch (err) {
        console.error(`Row ${i + 1} import failed:`, err);
        failures++;
        errorList.push(`Row ${i + 1}: ${err.message}`);
      }
    }

    setSuccessCount(success);
    setFailureCount(failures);
    setErrors(errorList);
    setImportStatus("complete");

    if (success > 0) {
      try {
        const refreshed = await api.get(`/faculty/${userId}/section/${section}`);
        onSuccess?.(refreshed);
      } catch (err) {
        console.error("Failed to refresh imported section data:", err);
      }
    }
  }

  const inputStyle = { width: "100%", background: "#f8fafc", border: "1.5px solid #e2e8f0", borderRadius: 10, padding: "12px", fontSize: 15, color: "#1e293b", outline: "none", boxSizing: "border-box" };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 999, padding: 20 }}>
      <div style={{ background: "#fff", borderRadius: 24, padding: "32px", width: "100%", maxWidth: 700, maxHeight: "90vh", overflowY: "auto", boxShadow: "0 32px 64px rgba(0,0,0,0.2)" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
          <h2 style={{ fontWeight: 800, color: "#0f2557", fontSize: 22 }}>📥 Import Articles</h2>
          <span onClick={onClose} style={{ fontSize: 24, cursor: "pointer", color: "#94a3b8" }}>✕</span>
        </div>

        {importStatus === null && (
          <div>
            <div style={{ background: "#f0f9ff", border: "1px solid #bfdbfe", borderRadius: 12, padding: 16, marginBottom: 20 }}>
              <p style={{ color: "#1e40af", fontWeight: 600, margin: "0 0 8px" }}>📋 How to use:</p>
              <ul style={{ color: "#1e40af", fontSize: 14, margin: "0 0 0 20px", paddingLeft: 0 }}>
                <li>Download the template</li>
                <li>Fill in your article data in Excel/CSV</li>
                <li>Select the file to preview and import</li>
              </ul>
            </div>

            <div style={{ display: "flex", gap: 12, marginBottom: 20 }}>
              <button onClick={downloadTemplate} style={{ flex: 1, background: "#10b981", color: "#fff", border: "none", borderRadius: 12, padding: "12px", fontWeight: 700, cursor: "pointer" }}>
                ⬇️ Download Template
              </button>
              <label style={{ flex: 1, background: "#3b82f6", color: "#fff", border: "none", borderRadius: 12, padding: "12px", fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                📤 Select File
                <input type="file" accept=".xlsx,.xls,.csv" onChange={handleFileSelect} style={{ display: "none" }} disabled={loading} />
              </label>
            </div>
          </div>
        )}

        {importStatus === "preview" && (
          <div>
            <div style={{ background: "#fef3c7", border: "1px solid #fcd34d", borderRadius: 12, padding: 12, marginBottom: 16, fontSize: 14, color: "#b45309" }}>
              📊 {previewData.length} articles ready to import
            </div>

            <div style={{ maxHeight: 300, overflowY: "auto", marginBottom: 16, border: "1px solid #e2e8f0", borderRadius: 12 }}>
              <table style={{ width: "100%", fontSize: 13, borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ background: "#f1f5f9", borderBottom: "1px solid #e2e8f0" }}>
                    <th style={{ padding: 10, textAlign: "left", fontWeight: 700, color: "#1e293b" }}>#</th>
                    <th style={{ padding: 10, textAlign: "left", fontWeight: 700, color: "#1e293b" }}>Title</th>
                    <th style={{ padding: 10, textAlign: "left", fontWeight: 700, color: "#1e293b" }}>Author</th>
                    <th style={{ padding: 10, textAlign: "left", fontWeight: 700, color: "#1e293b" }}>Journal</th>
                  </tr>
                </thead>
                <tbody>
                  {previewData.slice(0, 10).map((row, i) => (
                    <tr key={i} style={{ borderBottom: "1px solid #f1f5f9" }}>
                      <td style={{ padding: 10, color: "#64748b" }}>{i + 1}</td>
                      <td style={{ padding: 10, color: "#1e293b", fontWeight: 500, maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis" }}>{row.title_of_article || row.title || "—"}</td>
                      <td style={{ padding: 10, color: "#64748b", maxWidth: 150, overflow: "hidden", textOverflow: "ellipsis" }}>{row.author_names || "—"}</td>
                      <td style={{ padding: 10, color: "#64748b", maxWidth: 150, overflow: "hidden", textOverflow: "ellipsis" }}>{row.journal_details || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div style={{ display: "flex", gap: 12, marginBottom: 20 }}>
              <button onClick={() => { setImportStatus(null); setPreviewData([]); }} style={{ flex: 1, background: "#f1f5f9", color: "#475569", border: "none", borderRadius: 12, padding: "12px", fontWeight: 700, cursor: "pointer" }}>
                ↩️ Back
              </button>
              <button onClick={handleImport} disabled={loading} style={{ flex: 2, background: "#8b5cf6", color: "#fff", border: "none", borderRadius: 12, padding: "12px", fontWeight: 700, cursor: loading ? "not-allowed" : "pointer", opacity: loading ? 0.6 : 1 }}>
                ✅ Import {previewData.length} Articles
              </button>
            </div>
          </div>
        )}

        {importStatus === "importing" && (
          <div>
            <div style={{ background: "#dbeafe", border: "1px solid #93c5fd", borderRadius: 12, padding: 12, marginBottom: 16, fontSize: 14, color: "#0c4a6e", fontWeight: 600 }}>
              ⏳ Importing {importProgress} of {totalToImport}...
            </div>
            <div style={{ background: "#f1f5f9", borderRadius: 12, height: 8, overflow: "hidden", marginBottom: 16 }}>
              <div style={{ background: "#8b5cf6", height: "100%", width: `${(importProgress / totalToImport) * 100}%`, transition: "width 0.3s" }} />
            </div>
          </div>
        )}

        {importStatus === "complete" && (
          <div>
            <div style={{ background: "#f0fdf4", border: "1px solid #86efac", borderRadius: 12, padding: 16, marginBottom: 16 }}>
              <div style={{ fontSize: 18, fontWeight: 800, color: "#16a34a", marginBottom: 8 }}>✅ Import Complete!</div>
              <div style={{ fontSize: 14, color: "#16a34a", marginBottom: 4 }}>✅ {successCount} articles imported successfully</div>
              {failureCount > 0 && <div style={{ fontSize: 14, color: "#dc2626", marginBottom: 4 }}>❌ {failureCount} articles failed</div>}
            </div>

            {errors.length > 0 && (
              <div style={{ background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 12, padding: 12, marginBottom: 16, maxHeight: 200, overflowY: "auto" }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: "#dc2626", marginBottom: 8 }}>⚠️ Errors:</div>
                {errors.map((err, i) => (
                  <div key={i} style={{ fontSize: 12, color: "#991b1b", marginBottom: 4 }}>{err}</div>
                ))}
              </div>
            )}

            <button onClick={() => { setImportStatus(null); setPreviewData([]); onSuccess(); }} style={{ width: "100%", background: "#8b5cf6", color: "#fff", border: "none", borderRadius: 12, padding: "12px", fontWeight: 700, cursor: "pointer" }}>
              🎉 Done
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function EditProfileModal({ profile, userId, departments, colleges, designations, profileFields, onClose, onSuccess }) {
  const initialFormState = { 
    institution: "Adhiparasakthi Dental College and Hospital",
    designationHistory: (profile.designationHistory || [
      { dept: profile.department || "", designation: profile.designation || "", unit: "", fromDate: "", toDate: "", isMain: true }
    ]),
    ...profile 
  };
  const [form, setForm] = useState(initialFormState);
  const formDataRef = useRef(initialFormState); // Keep track of current form data to avoid stale closures
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [profileImage, setProfileImage] = useState(null);
  const [profileImagePreview, setProfileImagePreview] = useState(profile.profilePhotoURL || null);
  const [pendingFiles, setPendingFiles] = useState({});
  const photoInputRef = useRef(null);

  // Update both state (for rendering) and ref (for always-current access)
  // IMPORTANT: Use BOTH ref + functional setState to guarantee data persists during rapid input
  const set = (k, v) => {
    formDataRef.current = { ...formDataRef.current, [k]: v };  // Update ref synchronously (always current)
    setForm(prev => ({ ...prev, [k]: v }));  // Use functional setState for React guarantee
  };

  const handleDesigHistoryChange = (index, field, value) => {
    const newData = [...form.designationHistory];
    if (field === "isMain" && value === true) {
      newData.forEach((item, i) => item.isMain = (i === index));
      // Sync with core profile
      set("department", newData[index].dept);
      set("designation", newData[index].designation);
    } else {
      newData[index][field] = value;
    }
    set("designationHistory", newData);
  };
  const addDesigHistoryRow = () => set("designationHistory", [...form.designationHistory, { dept: "", designation: "", unit: "", fromDate: "", toDate: "", isMain: false }]);
  const removeDesigHistoryRow = (index) => set("designationHistory", form.designationHistory.filter((_, i) => i !== index));

  async function handleSave() {
    setLoading(true);
    setError("");
    try {
      // Always use ref for current form data to avoid stale state closures
      const currentFormData = formDataRef.current;
      console.log("[EditProfileModal] handleSave() called with form data from ref:", currentFormData);
      
      // Helper: get the key used in form for a field by its canonical name variants
      const getFieldKeyInternal = (fields, nameVariants) => {
        for (const variant of nameVariants) {
          const found = fields.find(f => (f.name || "").toLowerCase().trim() === variant.toLowerCase());
          if (found) return found.key || found.name.toLowerCase().replace(/[^a-z0-9]/g, "_");
        }
        return null;
      };

      // Resolve actual keys used for core fields from dynamic field definitions (profileFields)
      const nameKey = getFieldKeyInternal(profileFields, ["staff name", "name", "full name"]) || "name";
      const empKey = getFieldKeyInternal(profileFields, ["staff id", "emp id", "employee id", "empid"]) || "empId";
      const mobileKey = getFieldKeyInternal(profileFields, ["mobile", "contact number", "phone", "mobile number"]) || "mobile";
      const deptKey = getFieldKeyInternal(profileFields, ["department", "departments", "dept"]) || "department";
      const desigKey = getFieldKeyInternal(profileFields, ["designation", "designations"]) || "designation";

      const nameVal = currentFormData[nameKey] || currentFormData.name || "";
      const empVal = currentFormData[empKey] || currentFormData.empId || "";
      const mobileVal = currentFormData[mobileKey] || currentFormData.mobile || "";
      const deptVal = currentFormData[deptKey] || currentFormData.department || "";
      const desigVal = currentFormData[desigKey] || currentFormData.designation || "";

      // Sync aliases exactly like CreateFaculty.jsx
      const coreAliases = {};
      if (nameVal) coreAliases.name = nameVal;
      if (empVal) coreAliases.empId = empVal;
      if (mobileVal) coreAliases.mobile = mobileVal;
      if (deptVal) coreAliases.department = deptVal;
      if (desigVal) coreAliases.designation = desigVal;

      const updatedData = { 
        ...currentFormData,
        ...coreAliases
      };

      if (profileImage) {
        updatedData.profilePhotoURL = await compressImageToBase64(profileImage);
      }
      
      // Log what we're sending
      console.log(`[EditProfileModal] Updating profile for userId: ${userId}`);
      console.log(`[EditProfileModal] Data being sent:`, updatedData);
      console.log(`[EditProfileModal] Data keys:`, Object.keys(updatedData));
      
      // Send as JSON
      const response = await api.put(`/faculty/${userId}/profile`, updatedData);
      console.log(`[EditProfileModal] API response:`, response);
      onSuccess();
    } catch (err) { 
      console.error("Update Error:", err);
      alert("Error: " + err.message);
      setError("❌ Failed to update."); 
    }
    finally { setLoading(false); }
  }

  const inputStyle = { width: "100%", background: "#f8fafc", border: "1.5px solid #e2e8f0", borderRadius: 10, padding: "10px 14px", fontSize: 16, color: "#1e293b", outline: "none", boxSizing: "border-box", fontFamily: "Georgia, serif" };
  const labelStyle = { fontSize: 14, fontWeight: 700, color: "#374151", display: "block", marginBottom: 6 };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 999, padding: 20 }}>
      <div style={{ background: "#fff", borderRadius: 24, padding: "36px", width: "100%", maxWidth: 500, maxHeight: "90vh", overflowY: "auto", boxShadow: "0 32px 64px rgba(0,0,0,0.2)" }}>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 24 }}>
          <h2 style={{ fontWeight: 800, color: "#0f2557" }}>✏️ Edit Faculty Profile</h2>
          <span onClick={onClose} style={{ fontSize: 24, cursor: "pointer", color: "#94a3b8" }}>✕</span>
        </div>
        {/* Profile Photo Upload */}
        <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 20, background: "#f8fafc", borderRadius: 12, padding: "14px 16px", border: "1px solid #e2e8f0" }}>
          <div
            onClick={() => photoInputRef.current?.click()}
            style={{ width: 64, height: 64, borderRadius: "50%", border: "2px dashed #bfdbfe", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", overflow: "hidden", background: "#eaf1ff", flexShrink: 0 }}
          >
            {profileImagePreview
              ? <img src={profileImagePreview} alt="Preview" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
              : <span style={{ fontSize: 26, opacity: 0.5 }}>📷</span>
            }
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: "#374151", marginBottom: 6 }}>PROFILE PHOTO</div>
            <input
              ref={photoInputRef}
              type="file"
              accept="image/*"
              style={{ display: "none" }}
              onChange={e => {
                const file = e.target.files[0];
                if (file) { setProfileImage(file); setProfileImagePreview(URL.createObjectURL(file)); }
              }}
            />
            <button type="button" onClick={() => photoInputRef.current?.click()}
              style={{ background: "#2563eb", color: "#fff", border: "none", borderRadius: 8, padding: "6px 14px", fontWeight: 700, fontSize: 14, cursor: "pointer", marginRight: 8 }}>
              {profileImagePreview ? "🔄 Change" : "📤 Upload"}
            </button>
            {profileImage && <span style={{ fontSize: 13, color: "#16a34a", fontWeight: 600 }}>✅ {profileImage.name}</span>}
          </div>
        </div>
        {error && <div style={{ background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 10, padding: "10px", color: "#dc2626", fontSize: 15, marginBottom: 14 }}>{error}</div>}
        <div style={{ paddingBottom: 10 }}>
          {(() => {
            // Define core fields with grouping (Only keep Readonly Email as it's separate from dynamic fields)
            const coreFields = [
               { id: "email_readonly", type: "Readonly", name: "Email Address (Read-only)", val: form.email || "", group: "Contact Details" }
            ];

            // Combine with dynamic fields
            const allFormFields = [
              ...coreFields,
              ...profileFields.map(f => ({
                id: f.id,
                name: f.name,
                type: f.type,
                options: f.options,
                val: form[f.key || f.name.toLowerCase().replace(/[^a-z0-9]/g, "_")],
                group: f.group || "Other Details",
                required: f.required,
                key: f.key || f.name.toLowerCase().replace(/[^a-z0-9]/g, "_")
              }))
            ];

            // Group them
            const fieldGroups = allFormFields.reduce((acc, f) => {
              const g = f.group || "Other Details";
              if (!acc[g]) acc[g] = [];
              acc[g].push(f);
              return acc;
            }, {});

            // Sort groups
            const groupOrder = ["STAFF DETAILS", "PERSONAL DETAILS", "CONTACT DETAILS", "JOB DETAILS"];
            const sortedGroupKeys = Object.keys(fieldGroups).sort((a, b) => {
              const idxA = groupOrder.indexOf(a.toUpperCase());
              const idxB = groupOrder.indexOf(b.toUpperCase());
              if (idxA !== -1 && idxB !== -1) return idxA - idxB;
              if (idxA !== -1) return -1;
              if (idxB !== -1) return 1;
              return a.localeCompare(b);
            });

            return sortedGroupKeys.map(groupName => (
              <div key={groupName} style={{ marginBottom: 24, border: "1px solid #eef2f6", borderRadius: 16, overflow: "hidden" }}>
                <div style={{ padding: "10px 16px", background: "#f8fafc", borderBottom: "1px solid #eef2f6", fontSize: 13, fontWeight: 900, color: "#475569", letterSpacing: 1 }}>
                  {groupName.toUpperCase()}
                </div>
                <div style={{ padding: 16, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
                  {fieldGroups[groupName].map(f => {
                    const k = f.id === "email_readonly" ? "email" : (f.key || f.id);
                    const fieldName = (f.name || "").toLowerCase().trim();
                    const label = f.name;
                    const type = (f.type || "text").toLowerCase();
                    const isState = fieldName === "state" || fieldName === "state/ut";
                    const isDistrict = fieldName === "district";

                    return (
                      <div key={f.id} style={{ gridColumn: type === "textarea" ? "1 / -1" : "auto" }}>
                        <label style={labelStyle}>{label} {f.required && <span style={{ color: "#dc2626" }}>*</span>}</label>
                        {f.type === "Readonly" ? (
                          <input value={f.val} disabled style={{ ...inputStyle, background: "#f1f5f9", cursor: "not-allowed" }} />
                        ) : isState ? (
                          <select value={form[k] || ""} onChange={e => {
                            set(k, e.target.value);
                            const dField = allFormFields.find(fd => (fd.name || "").toLowerCase().trim() === "district");
                            if (dField) set(dField.key || dField.id, "");
                          }} style={{ ...inputStyle, cursor: "pointer" }}>
                            <option value="">-- Select State --</option>
                            {Object.keys(INDIAN_STATES_DISTRICTS).map(s => <option key={s} value={s}>{s}</option>)}
                          </select>
                        ) : isDistrict ? (
                          <select value={form[k] || ""} onChange={e => set(k, e.target.value)} style={{ ...inputStyle, cursor: "pointer" }} disabled={!form[allFormFields.find(fd => (fd.name || "").toLowerCase().trim() === "state" || (fd.name || "").toLowerCase().trim() === "state/ut")?.key || "state"]}>
                            <option value="">-- Select District --</option>
                            {(() => {
                              const sField = allFormFields.find(fd => (fd.name || "").toLowerCase().trim() === "state" || (fd.name || "").toLowerCase().trim() === "state/ut");
                              const selectedState = form[sField?.key || sField?.id || "state"];
                              return (selectedState && INDIAN_STATES_DISTRICTS[selectedState]) ? INDIAN_STATES_DISTRICTS[selectedState].map(d => <option key={d} value={d}>{d}</option>) : null;
                            })()}
                          </select>
                        ) : type === "dropdown" ? (
                          <select value={form[k] || ""} onChange={e => set(k, e.target.value)} style={{ ...inputStyle, cursor: "pointer" }}>
                            <option value="">-- Select --</option>
                            {(f.options || []).map(o => <option key={o} value={o}>{o}</option>)}
                          </select>
                        ) : type === "textarea" ? (
                          <textarea value={form[k] || ""} onChange={e => set(k, e.target.value)} rows={3} style={{ ...inputStyle, resize: "vertical" }} />
                        ) : type === "image" ? (
                          <div>
                            {form[k] && typeof form[k] === "string" && form[k].startsWith("http") && !pendingFiles[k] && (
                              <div style={{ fontSize: 13, marginBottom: 8, display: "flex", alignItems: "center", gap: 6 }}>
                                <img src={form[k]} alt="Current" style={{ width: 60, height: 60, objectFit: "cover", borderRadius: 8 }} />
                                <a href={form[k]} target="_blank" rel="noreferrer" style={{ color: "#2563eb", textDecoration: "none", fontWeight: 700 }}>View Full</a>
                              </div>
                            )}
                            <input type="file" accept="image/*" onChange={e => {
                              const file = e.target.files[0];
                              if (file) {
                                if (file.size > 200 * 1024) {
                                  alert("❌ Image size must be below 200KB");
                                  e.target.value = "";
                                  return;
                                }
                                if (!file.type.startsWith("image/")) {
                                  alert("❌ Please select a valid image file");
                                  e.target.value = "";
                                  return;
                                }
                                setPendingFiles(prev => ({ ...prev, [k]: file }));
                              }
                            }} style={{ ...inputStyle, padding: "6px 10px" }} />
                            {pendingFiles[k] && <div style={{ fontSize: 12, color: "#16a34a", marginTop: 4 }}>✅ Selected: {(pendingFiles[k].size / 1024).toFixed(1)}KB</div>}
                          </div>
                        ) : type === "file" ? (
                          <div>
                            {form[k] && typeof form[k] === "string" && form[k].startsWith("http") && (
                              <div style={{ fontSize: 13, marginBottom: 4, display: "flex", alignItems: "center", gap: 6 }}>
                                <span style={{ color: "#16a34a", fontWeight: 700 }}>✅ Exists: </span>
                                <a href={form[k]} target="_blank" rel="noreferrer" style={{ color: "#2563eb", textDecoration: "none" }}>View</a>
                              </div>
                            )}
                            <input type="file" onChange={e => setPendingFiles(prev => ({ ...prev, [k]: e.target.files[0] }))} style={{ ...inputStyle, padding: "6px 10px" }} />
                          </div>
                        ) : (
                          <input type={type === "number" ? "number" : type === "date" ? "date" : "text"}
                            value={form[k] || ""} onChange={e => set(k, e.target.value)}
                            style={inputStyle} />
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ));
          })()}
        </div>

        {/* Designation History */}
        <div style={{ marginTop: 24, borderTop: "2px solid #f1f5f9", paddingTop: 20 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
            <h3 style={{ margin: 0, fontSize: 15, fontWeight: 800, color: "#0f2557" }}>Institutional Designation History</h3>
            <button onClick={addDesigHistoryRow} style={{ background: "#2563eb", color: "#fff", border: "none", borderRadius: 8, padding: "4px 12px", fontWeight: 700, cursor: "pointer", fontSize: 14 }}>+ Add Row</button>
          </div>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 600 }}>
              <thead>
                <tr style={{ background: "#f8fafc", fontSize: 13, textAlign: "left" }}>
                  <th style={{ padding: "8px" }}>S.No</th>
                  <th style={{ padding: "8px" }}>Dept</th>
                  <th style={{ padding: "8px" }}>Desig</th>
                  <th style={{ padding: "8px" }}>Unit</th>
                  <th style={{ padding: "8px" }}>From</th>
                  <th style={{ padding: "8px" }}>To</th>
                  <th style={{ padding: "8px", textAlign: "center" }}>Main</th>
                  <th style={{ padding: "8px" }}></th>
                </tr>
              </thead>
              <tbody>
                {form.designationHistory.map((des, idx) => (
                  <tr key={idx} style={{ borderBottom: "1px solid #f1f5f9" }}>
                    <td style={{ padding: "6px 4px", fontSize: 14 }}>{idx + 1}</td>
                    <td style={{ padding: "6px 4px" }}>
                       <select value={des.dept} onChange={e => handleDesigHistoryChange(idx, "dept", e.target.value)} style={{ ...inputStyle, padding: "6px 8px", fontSize: 14 }}>
                        <option value="">--Sel--</option>
                        {departments.map(o => <option key={o} value={o}>{o}</option>)}
                      </select>
                    </td>
                    <td style={{ padding: "6px 4px" }}>
                       <select value={des.designation} onChange={e => handleDesigHistoryChange(idx, "designation", e.target.value)} style={{ ...inputStyle, padding: "6px 8px", fontSize: 14 }}>
                        <option value="">--Sel--</option>
                        {designations.map(o => <option key={o} value={o}>{o}</option>)}
                      </select>
                    </td>
                    <td style={{ padding: "6px 4px" }}>
                       <select value={des.unit} onChange={e => handleDesigHistoryChange(idx, "unit", e.target.value)} style={{ ...inputStyle, padding: "6px 8px", fontSize: 14 }}>
                        <option value="">--Sel--</option>
                        {["I", "II", "III", "IV", "V"].map(o => <option key={o} value={o}>{o}</option>)}
                      </select>
                    </td>
                    <td style={{ padding: "6px 4px" }}><input type="date" value={des.fromDate} onChange={e => handleDesigHistoryChange(idx, "fromDate", e.target.value)} style={{ ...inputStyle, padding: "4px 6px", fontSize: 13 }} /></td>
                    <td style={{ padding: "6px 4px" }}><input type="date" value={des.toDate} onChange={e => handleDesigHistoryChange(idx, "toDate", e.target.value)} style={{ ...inputStyle, padding: "4px 6px", fontSize: 13 }} /></td>
                    <td style={{ padding: "6px 4px", textAlign: "center" }}>
                      <input type="checkbox" checked={des.isMain} onChange={e => handleDesigHistoryChange(idx, "isMain", e.target.checked)} style={{ transform: "scale(1.1)" }} />
                    </td>
                    <td style={{ padding: "6px 4px" }}>
                      {form.designationHistory.length > 1 && (
                        <button onClick={() => removeDesigHistoryRow(idx)} style={{ background: "#fef2f2", color: "#dc2626", border: "none", borderRadius: 6, width: 24, height: 24, cursor: "pointer" }}>×</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <div style={{ display: "flex", gap: 12, marginTop: 24 }}>
          <button onClick={onClose} style={{ flex: 1, background: "#f1f5f9", color: "#475569", border: "none", borderRadius: 12, padding: "13px", fontWeight: 700, cursor: "pointer" }}>Cancel</button>
          <button onClick={handleSave} disabled={loading} style={{ flex: 2, background: loading ? "#93c5fd" : "#2563eb", color: "#fff", border: "none", borderRadius: 12, padding: "13px", fontWeight: 800, cursor: loading ? "not-allowed" : "pointer" }}>
            {loading ? "⏳ Saving..." : "✅ Save Changes"}
          </button>
        </div>
      </div>
    </div>
  );
}
